import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { GlassCard, GlassButton } from '../../components/ui/GlassComponents';
import SystemHealthMonitor from '../../components/admin/SystemHealthMonitor';
import AnnouncementManager from '../../components/admin/AnnouncementManager';
import StalledSignups from '../../components/admin/StalledSignups';
import { FiUsers, FiCreditCard, FiActivity, FiTrendingUp, FiMessageSquare, FiMail } from 'react-icons/fi';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalTeams: 0,
    totalUsers: 0,
    activeSubscriptions: 0,
    recentSignups: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/admin/dashboard');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching admin dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <GlassCard className="p-8 text-center max-w-md mx-auto mt-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading admin dashboard...</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Admin Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Monitor and manage your LeaveWise platform</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-lg p-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'overview'
                  ? 'bg-white/80 dark:bg-gray-700/80 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('health')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'health'
                  ? 'bg-white/80 dark:bg-gray-700/80 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              System Health
            </button>
            <button
              onClick={() => setActiveTab('announcements')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'announcements'
                  ? 'bg-white/80 dark:bg-gray-700/80 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <FiMessageSquare className="w-4 h-4" />
              Announcements
            </button>
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <GlassCard className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-500 dark:text-blue-300 mr-4">
                    <FiUsers className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Teams</p>
                    <p className="text-2xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {stats.totalTeams}
                    </p>
                  </div>
                </div>
              </GlassCard>
              
              <GlassCard className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100 dark:bg-green-900 text-green-500 dark:text-green-300 mr-4">
                    <FiUsers className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</p>
                    <p className="text-2xl font-semibold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                      {stats.totalUsers}
                    </p>
                  </div>
                </div>
              </GlassCard>
              
              <GlassCard className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-500 dark:text-purple-300 mr-4">
                    <FiCreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Subscriptions</p>
                    <p className="text-2xl font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      {stats.activeSubscriptions}
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>

            {/* Stalled Signups — actionable: people who started signup but
                didn't finish at Stripe checkout. One-click to email them. */}
            <StalledSignups />

            {/* Recent Signups */}
            <GlassCard className="overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
                <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Recent Signups
                </h2>
              </div>
              <div className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
                {stats.recentSignups.length > 0 ? (
                  stats.recentSignups.map((user) => (
                    <div key={user.id} className="px-6 py-4 hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {user.avatar ? (
                              <img className="h-10 w-10 rounded-full" src={user.avatar} alt="" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                                <span className="text-sm font-medium text-white">
                                  {user.name.charAt(0)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {format(new Date(user.createdAt), 'MMM dd, yyyy')}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    <FiUsers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No recent signups</p>
                  </div>
                )}
              </div>
            </GlassCard>

            {/* Quick Actions */}
            <GlassCard className="overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
                <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Quick Actions
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link
                  to="/admin/teams"
                  className="group flex items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-800/30 dark:hover:to-purple-800/30 transition-all duration-200 border border-blue-200/50 dark:border-blue-700/50"
                >
                  <FiUsers className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-3 group-hover:scale-110 transition-transform" />
                  <span className="text-gray-900 dark:text-white font-medium">Manage Teams</span>
                </Link>
                
                <Link
                  to="/admin/users"
                  className="group flex items-center p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg hover:from-green-100 hover:to-blue-100 dark:hover:from-green-800/30 dark:hover:to-blue-800/30 transition-all duration-200 border border-green-200/50 dark:border-green-700/50"
                >
                  <FiUsers className="h-6 w-6 text-green-500 dark:text-green-400 mr-3 group-hover:scale-110 transition-transform" />
                  <span className="text-gray-900 dark:text-white font-medium">Manage Users</span>
                </Link>
                
                <Link
                  to="/admin/payments"
                  className="group flex items-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-800/30 dark:hover:to-pink-800/30 transition-all duration-200 border border-purple-200/50 dark:border-purple-700/50"
                >
                  <FiCreditCard className="h-6 w-6 text-purple-500 dark:text-purple-400 mr-3 group-hover:scale-110 transition-transform" />
                  <span className="text-gray-900 dark:text-white font-medium">Manage Payments</span>
                </Link>
                
                <Link
                  to="/admin/logs"
                  className="group flex items-center p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg hover:from-indigo-100 hover:to-blue-100 dark:hover:from-indigo-800/30 dark:hover:to-blue-800/30 transition-all duration-200 border border-indigo-200/50 dark:border-indigo-700/50"
                >
                  <FiActivity className="h-6 w-6 text-indigo-500 dark:text-indigo-400 mr-3 group-hover:scale-110 transition-transform" />
                  <span className="text-gray-900 dark:text-white font-medium">Activity Logs</span>
                </Link>

                <Link
                  to="/admin/emails"
                  className="group flex items-center p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg hover:from-amber-100 hover:to-orange-100 dark:hover:from-amber-800/30 dark:hover:to-orange-800/30 transition-all duration-200 border border-amber-200/50 dark:border-amber-700/50"
                >
                  <FiMail className="h-6 w-6 text-amber-500 dark:text-amber-400 mr-3 group-hover:scale-110 transition-transform" />
                  <span className="text-gray-900 dark:text-white font-medium">Emails</span>
                </Link>
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'health' && (
          <SystemHealthMonitor />
        )}

        {activeTab === 'announcements' && (
          <AnnouncementManager />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
